/**
 * Lunimar Collective realm identities for beta modules.
 *
 * Each beta module is a different location within the Lunimar world. The
 * realm map controls accent palette, environmental motif, and the realm
 * subtitle shown in the mission-control header.
 *
 * Accent colors are HSL strings (no `hsl(...)` wrapper) so they can be
 * dropped into inline styles via `hsl(var(--...))`-style composition or
 * `hsl(${accent})`.
 */

export type RealmKey =
  | "budget_builder"
  | "credit_management"
  | "scam_defense"
  | "family_budget"
  | "risk_protection"
  | "recovery_mode"
  | "tax_strategy"
  | "entrepreneurship"
  | "wealth_strategy"
  | "career_path"
  | "economics"
  | "development"
  | "investing"
  | "homebuying"
  | "re_investing"
  | "vehicle_ownership"
  | "account_setup"
  | "default";

export interface LunimarRealm {
  key: RealmKey;
  /** Display title shown in the header. */
  moduleName: string;
  /** Subtitle shown beneath the module name. */
  realmName: string;
  /** Short atmospheric description (used for ambient layer hints / a11y). */
  motif: string;
  /** Primary accent — controls glow + active states. HSL triplet. */
  primaryAccent: string;
  /** Secondary accent — ambient environment. HSL triplet. */
  secondaryAccent: string;
  /** Tertiary accent — progress / reward milestones. HSL triplet. */
  tertiaryAccent: string;
}

export const LUNIMAR_REALMS: Record<RealmKey, LunimarRealm> = {
  budget_builder: {
    key: "budget_builder",
    moduleName: "Budget Builder",
    realmName: "Cashflow Atrium",
    motif: "income streams, floating ledgers, coin rings",
    primaryAccent: "195 95% 62%",
    secondaryAccent: "262 78% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  credit_management: {
    key: "credit_management",
    moduleName: "Credit Management",
    realmName: "Credit Observatory",
    motif: "trust ledgers, vaulted glyphs, slow-pulse credit lines",
    primaryAccent: "168 85% 55%",
    secondaryAccent: "230 72% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  scam_defense: {
    key: "scam_defense",
    moduleName: "Scam Defense",
    realmName: "Signal Watchtower",
    motif: "sentinel scans, warning glyphs, encrypted signal arcs",
    primaryAccent: "12 92% 62%",
    secondaryAccent: "278 70% 62%",
    tertiaryAccent: "44 92% 64%",
  },
  family_budget: {
    key: "family_budget",
    moduleName: "Family Budget & Planning",
    realmName: "Kinship Hearth",
    motif: "shared ledgers, generational arcs, family glyphs",
    primaryAccent: "24 92% 64%",
    secondaryAccent: "262 70% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  risk_protection: {
    key: "risk_protection",
    moduleName: "Risk Protection",
    realmName: "Sentinel Bastion",
    motif: "shield lattices, risk meters, coverage halos",
    primaryAccent: "210 90% 64%",
    secondaryAccent: "278 65% 62%",
    tertiaryAccent: "44 92% 64%",
  },
  recovery_mode: {
    key: "recovery_mode",
    moduleName: "Recovery Mode",
    realmName: "Phoenix Reclaim",
    motif: "regrowth arcs, recovery embers, stabilizing rings",
    primaryAccent: "152 70% 55%",
    secondaryAccent: "24 88% 60%",
    tertiaryAccent: "44 92% 64%",
  },
  tax_strategy: {
    key: "tax_strategy",
    moduleName: "Tax Strategy",
    realmName: "Ledger Citadel",
    motif: "tax brackets, deduction trails, account vaults",
    primaryAccent: "188 88% 58%",
    secondaryAccent: "262 70% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  entrepreneurship: {
    key: "entrepreneurship",
    moduleName: "Entrepreneurship",
    realmName: "Founder Forge",
    motif: "startup blueprints, revenue engines, product sparks",
    primaryAccent: "32 95% 60%",
    secondaryAccent: "278 72% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  wealth_strategy: {
    key: "wealth_strategy",
    moduleName: "Wealth Strategy",
    realmName: "Risk Horizon",
    motif: "market waves, portfolio orbs, allocation rings",
    primaryAccent: "148 70% 55%",
    secondaryAccent: "262 72% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  career_path: {
    key: "career_path",
    moduleName: "Career Path",
    realmName: "Pathfinder Atelier",
    motif: "career arcs, skill constellations, milestone lights",
    primaryAccent: "210 92% 64%",
    secondaryAccent: "278 70% 62%",
    tertiaryAccent: "44 92% 64%",
  },
  economics: {
    key: "economics",
    moduleName: "Economics",
    realmName: "Macro Nexus",
    motif: "supply-demand flows, market currents, policy arcs",
    primaryAccent: "230 80% 66%",
    secondaryAccent: "188 80% 58%",
    tertiaryAccent: "44 92% 64%",
  },
  development: {
    key: "development",
    moduleName: "Development",
    realmName: "Builder's Skyline",
    motif: "blueprints, scaffolds, capital stacks, project lights",
    primaryAccent: "44 92% 60%",
    secondaryAccent: "210 80% 60%",
    tertiaryAccent: "262 72% 64%",
  },
  investing: {
    key: "investing",
    moduleName: "Investing",
    realmName: "Risk Horizon",
    motif: "portfolio orbs, volatility rings, time arcs",
    primaryAccent: "152 70% 55%",
    secondaryAccent: "262 72% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  homebuying: {
    key: "homebuying",
    moduleName: "Homeownership",
    realmName: "Equity Citadel",
    motif: "home holograms, mortgage layers, equity growth paths",
    primaryAccent: "188 80% 60%",
    secondaryAccent: "210 80% 60%",
    tertiaryAccent: "44 92% 64%",
  },
  re_investing: {
    key: "re_investing",
    moduleName: "Real Estate Investing",
    realmName: "Skyline Holdings",
    motif: "rental yields, cap-rate rings, property arcs",
    primaryAccent: "168 85% 55%",
    secondaryAccent: "230 72% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  vehicle_ownership: {
    key: "vehicle_ownership",
    moduleName: "Car Buying",
    realmName: "Mobility Exchange",
    motif: "vehicle holograms, loan paths, ownership trails",
    primaryAccent: "210 92% 64%",
    secondaryAccent: "230 72% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  account_setup: {
    key: "account_setup",
    moduleName: "Account Setup",
    realmName: "Onboarding Atrium",
    motif: "calibrating panels, intake signals, identity glyphs",
    primaryAccent: "195 95% 62%",
    secondaryAccent: "262 78% 64%",
    tertiaryAccent: "44 92% 64%",
  },
  default: {
    key: "default",
    moduleName: "Mission",
    realmName: "Lunimar Collective",
    motif: "drifting glyphs, ambient nebula, mission lights",
    primaryAccent: "210 90% 64%",
    secondaryAccent: "262 72% 64%",
    tertiaryAccent: "44 92% 64%",
  },
};

export function getRealm(key: RealmKey): LunimarRealm {
  return LUNIMAR_REALMS[key] ?? LUNIMAR_REALMS.default;
}

/**
 * Resolve a realm from a free-form module title. Used by the legacy
 * `ModuleShell` so all existing modules get a distinct Lunimar realm
 * without each page having to pass a `realmKey` prop.
 */
export function resolveRealmByTitle(title: string): LunimarRealm {
  const t = title.toLowerCase();
  if (t.includes("budget builder")) return LUNIMAR_REALMS.budget_builder;
  if (t.includes("family")) return LUNIMAR_REALMS.family_budget;
  if (t.includes("credit")) return LUNIMAR_REALMS.credit_management;
  if (t.includes("scam")) return LUNIMAR_REALMS.scam_defense;
  if (t.includes("risk protection") || t.includes("insurance"))
    return LUNIMAR_REALMS.risk_protection;
  if (t.includes("recovery")) return LUNIMAR_REALMS.recovery_mode;
  if (t.includes("tax")) return LUNIMAR_REALMS.tax_strategy;
  if (t.includes("entrepreneur") || t.includes("founder") || t.includes("business"))
    return LUNIMAR_REALMS.entrepreneurship;
  if (t.includes("wealth")) return LUNIMAR_REALMS.wealth_strategy;
  if (t.includes("career")) return LUNIMAR_REALMS.career_path;
  if (t.includes("econ")) return LUNIMAR_REALMS.economics;
  if (t.includes("development")) return LUNIMAR_REALMS.development;
  if (t.includes("real estate") || t.includes("re-invest") || t.includes("re investing"))
    return LUNIMAR_REALMS.re_investing;
  if (t.includes("home") || t.includes("mortgage")) return LUNIMAR_REALMS.homebuying;
  if (t.includes("car") || t.includes("vehicle") || t.includes("auto"))
    return LUNIMAR_REALMS.vehicle_ownership;
  if (t.includes("invest")) return LUNIMAR_REALMS.investing;
  if (t.includes("account setup") || t.includes("onboarding"))
    return LUNIMAR_REALMS.account_setup;
  return LUNIMAR_REALMS.default;
}